import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { practitionerLinkApi } from "../api/practitionerLink";

// "What your practitioner can see" — the client's control surface for
// the one opt-in channel through the journal block.
//
// Posture: the default is nothing, and the screen has to look like it.
// It opens on the receipt (what is currently shared, with whom), not on
// the picker, so the first thing you see is the truth about disclosure
// rather than an invitation to disclose. Choosing is a deliberate second
// step, per entry, in a mode you pick.
//
// Two modes per entry, narrowest first:
//   · Takeaway  — the one-sentence summary Haiven already showed you
//   · Full entry — the reflection as you wrote it
//
// Nothing here is a suggestion. Copy stays in Reflect/Contextualize
// register: it says what is shared and what happens when you stop,
// never that sharing more would be better.

function fmtDay(iso) {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
      month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

const MODE_LABEL = { summary: "Takeaway", excerpt: "Full entry" };

// Which surface an entry was written on. Shown on the card so the
// client knows which piece of writing they're deciding about — a
// check-in reflection and a free-write from the same day are different
// things, and consenting to one is not consenting to the other.
const SOURCE_LABEL = { checkin: "Check-in", journal: "Free-write" };

// Both candidates and receipt rows carry `entry_id`. The fallback to
// `checkin_id` keeps this component working against a backend that
// predates the discriminator.
function entryIdOf(row) {
  return row?.entry_id ?? row?.checkin_id;
}

// `focusEntryId` is the Journal tab's entry point: opened from one
// specific entry, the modal skips straight to the picker and marks that
// entry's card, so the client lands on the decision they came to make
// rather than hunting for it. Opened from the practitioner card (no
// id), behaviour is unchanged — the receipt first, the picker second.
export default function SharedEntriesModal({ link, onClose, onChange, focusEntryId = null }) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [picking, setPicking] = useState(Boolean(focusEntryId));
  const focusRef = useRef(null);

  // Whether Haiven is allowed to read written entries at all. A free-
  // write entry only becomes a shareable candidate once an extraction
  // row exists for it, and that extraction is gated on this flag — so
  // an unconsented user sees an empty picker for a reason that has
  // nothing to do with practitioner sharing. Naming the real blocker is
  // this value's only job here; it gates no request and changes no
  // eligibility. Sharing itself remains governed by research_consent
  // (the `eligible` flag below), and neither has any bearing on
  // scoring, which excludes free-write text structurally.
  const aiConsented = user?.ai_data_consent === true;

  const [shared, setShared] = useState([]);
  const [visible, setVisible] = useState(true);
  const [practitionerName, setPractitionerName] = useState(
    link?.practitioner_name || "your practitioner"
  );
  const [eligible, setEligible] = useState(true);
  const [candidates, setCandidates] = useState([]);

  const linkId = link?.id;

  const load = useCallback(async () => {
    if (!linkId || !token) return;
    setError(null);
    try {
      const [receipt, picker] = await Promise.all([
        practitionerLinkApi.sharedEntries(linkId, token),
        practitionerLinkApi.shareableEntries(linkId, token, 30),
      ]);
      setShared(receipt?.shared || []);
      setVisible(receipt?.visible_to_practitioner !== false);
      if (receipt?.practitioner_name) setPractitionerName(receipt.practitioner_name);
      setEligible(picker?.eligible !== false);
      setCandidates(picker?.entries || []);
    } catch {
      setError("Something went wrong loading what's shared.");
    } finally {
      setLoading(false);
    }
  }, [linkId, token]);

  useEffect(() => { load(); }, [load]);

  // Bring the entry the client arrived from into view once the picker
  // has rendered it. Best-effort — jsdom and some WebKit builds no-op
  // on scrollIntoView, and a missed scroll is cosmetic, not a failure.
  useEffect(() => {
    if (!focusEntryId || loading) return;
    focusRef.current?.scrollIntoView?.({ block: "center" });
  }, [focusEntryId, loading, candidates]);

  const sharedIds = new Set(shared.map(entryIdOf));

  // `source` routes the id into the right request field; the rest of
  // this component stays source-agnostic, because the candidate shape
  // is identical across both surfaces by design.
  async function share(entryId, mode, source) {
    setBusyId(entryId);
    setError(null);
    try {
      await practitionerLinkApi.shareEntries(linkId, [entryId], mode, token, source);
      await load();
      onChange?.();
    } catch {
      setError("That entry didn't get shared — nothing changed.");
    } finally {
      setBusyId(null);
    }
  }

  async function revoke(entryId) {
    setBusyId(entryId);
    setError(null);
    try {
      await practitionerLinkApi.revokeEntry(linkId, entryId, token);
      await load();
      onChange?.();
    } catch {
      setError("That entry is still shared — the change didn't go through.");
    } finally {
      setBusyId(null);
    }
  }

  async function revokeAll() {
    setBusyId("__all__");
    setError(null);
    try {
      await practitionerLinkApi.revokeAllEntries(linkId, token);
      await load();
      onChange?.();
    } catch {
      setError("Sharing hasn't stopped — the change didn't go through.");
    } finally {
      setBusyId(null);
    }
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="What your practitioner can see"
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: P.bg,
        overflowY: "auto",
        paddingTop: "max(28px, calc(env(safe-area-inset-top) + 12px))",
        paddingBottom: "max(28px, calc(env(safe-area-inset-bottom) + 12px))",
      }}
    >
      <div style={{ maxWidth: 420, margin: "0 auto", padding: "0 24px" }}>
        <div style={{
          fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
          color: P.primary, fontWeight: 600, marginBottom: 14,
        }}>
          Written entries
        </div>

        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 26, fontStyle: "italic", fontWeight: 400,
          color: P.text, lineHeight: 1.3, marginBottom: 12,
        }}>
          What {practitionerName} can see
        </h1>

        {loading ? (
          <p style={{ fontSize: 14, color: P.textMute, fontWeight: 300 }}>Loading…</p>
        ) : (
          <>
            {/* ── The receipt ──────────────────────────────────────── */}
            {shared.length === 0 ? (
              <p style={{ fontSize: 14.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7, marginBottom: 26 }}>
                None of your written entries. {practitionerName} sees completion
                and signal trends only — nothing you've written reaches them
                unless you choose an entry below.
              </p>
            ) : (
              <>
                <p style={{ fontSize: 14.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7, marginBottom: 16 }}>
                  {shared.length === 1
                    ? "One entry you chose, plus completion and signal trends."
                    : `${shared.length} entries you chose, plus completion and signal trends.`}
                  {" "}Everything else you've written stays private.
                </p>

                {!visible && (
                  <p style={{
                    fontSize: 13, color: P.textMid, fontWeight: 300, lineHeight: 1.6,
                    background: P.surfaceAlt, borderRadius: 12, padding: "12px 14px",
                    marginBottom: 16,
                  }}>
                    Nothing is reaching {practitionerName} right now — the link
                    with them is paused. These stay chosen if it resumes.
                  </p>
                )}

                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px" }}>
                  {shared.map((s) => {
                    const id = entryIdOf(s);
                    const sourceLabel = SOURCE_LABEL[s.source];
                    return (
                    <li
                      key={id}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 12, padding: "12px 0", borderTop: `1px solid ${P.border}`,
                      }}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 14, color: P.text, fontWeight: 400 }}>
                          {fmtDay(s.date)}
                        </span>
                        <span style={{ display: "block", fontSize: 11.5, color: P.textMute, marginTop: 2 }}>
                          {sourceLabel ? `${sourceLabel} · ` : ""}
                          {MODE_LABEL[s.share_mode] || MODE_LABEL.summary}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => revoke(id)}
                        disabled={busyId !== null}
                        aria-label={`Stop sharing the entry from ${fmtDay(s.date)}`}
                        style={{
                          background: "none", border: "none", padding: "6px 4px",
                          fontSize: 12.5, color: P.textMid, flexShrink: 0,
                          cursor: busyId !== null ? "default" : "pointer",
                        }}
                      >
                        {busyId === id ? "Stopping…" : "Stop sharing"}
                      </button>
                    </li>
                    );
                  })}
                </ul>

                <button
                  type="button"
                  onClick={revokeAll}
                  disabled={busyId !== null}
                  style={{
                    background: "none", border: "none", padding: "4px 0",
                    fontSize: 12.5, color: P.textMute, marginBottom: 22,
                    cursor: busyId !== null ? "default" : "pointer",
                  }}
                >
                  {busyId === "__all__" ? "Stopping…" : "Stop sharing everything"}
                </button>
              </>
            )}

            {error && (
              <p role="alert" style={{ fontSize: 13, color: P.text, background: P.surfaceAlt, borderRadius: 12, padding: "10px 12px", marginBottom: 16 }}>
                {error}
              </p>
            )}

            {/* ── The picker ───────────────────────────────────────── */}
            {!eligible ? (
              <p style={{
                fontSize: 13.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7,
                background: P.surface, borderRadius: 14, padding: "16px 18px",
              }}>
                Sharing written entries isn't available on your account — it
                depends on a consent choice made during setup. Nothing you've
                written reaches {practitionerName}; they see completion and
                signal trends only.
              </p>
            ) : !picking ? (
              <button
                type="button"
                onClick={() => setPicking(true)}
                style={{
                  width: "100%", padding: 15, background: "none",
                  border: `0.5px solid ${P.border}`, borderRadius: 14,
                  color: P.text, fontSize: 14, cursor: "pointer",
                }}
              >
                Choose an entry to share
              </button>
            ) : candidates.length === 0 ? (
              // Two different silences, and they used to look identical.
              // With research_consent granted the picker can still come
              // back empty because Haiven was never allowed to read the
              // writing in the first place — a different consent, in a
              // different place, with a different meaning. Saying "you
              // haven't written anything" to someone who writes daily is
              // a false statement about their own history, so the
              // unconsented case names its actual cause.
              !aiConsented ? (
                <p style={{
                  fontSize: 13.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7,
                  background: P.surface, borderRadius: 14, padding: "16px 18px",
                }}>
                  Written reflections appear here once AI data-sharing is on in
                  Settings — that's the setting that lets Haiven read them. It's
                  separate from this one, and turning it on shares nothing with
                  {" "}{practitionerName} on its own.
                </p>
              ) : (
                <p style={{ fontSize: 13.5, color: P.textMute, fontWeight: 300, lineHeight: 1.7 }}>
                  No written reflections from the last 30 days yet.
                </p>
              )
            ) : (
              <>
                <div style={{
                  fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                  color: P.primary, fontWeight: 600, margin: "6px 0 12px",
                }}>
                  Your last 30 days
                </div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {candidates.map((c) => {
                    const id = entryIdOf(c);
                    const isShared = sharedIds.has(id);
                    const sourceLabel = SOURCE_LABEL[c.source];
                    const isFocused = focusEntryId != null && id === focusEntryId;
                    return (
                      <li
                        key={id}
                        ref={isFocused ? focusRef : null}
                        style={{
                          background: P.surface, borderRadius: 14,
                          padding: "14px 16px", marginBottom: 10,
                          border: isFocused
                            ? `1px solid ${P.primary}`
                            : "1px solid transparent",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                          <span style={{ fontSize: 13, color: P.text, fontWeight: 400 }}>
                            {fmtDay(c.date)}
                            {sourceLabel && (
                              <span style={{ fontSize: 11, color: P.textMute, marginLeft: 8 }}>
                                {sourceLabel}
                              </span>
                            )}
                          </span>
                          <span style={{ fontSize: 11, color: P.textMute }}>{c.domain}</span>
                        </div>
                        {c.summary && (
                          <p style={{ fontSize: 12.5, color: P.textMid, fontWeight: 300, lineHeight: 1.6, margin: "0 0 6px" }}>
                            {c.summary}
                          </p>
                        )}
                        <p style={{ fontSize: 13, color: P.text, fontWeight: 300, lineHeight: 1.65, margin: "0 0 10px" }}>
                          {c.text}
                        </p>
                        {isShared ? (
                          <button
                            type="button"
                            onClick={() => revoke(id)}
                            disabled={busyId !== null}
                            style={{
                              background: "none", border: "none", padding: "4px 0",
                              fontSize: 12.5, color: P.textMid,
                              cursor: busyId !== null ? "default" : "pointer",
                            }}
                          >
                            Shared as {MODE_LABEL[c.share_mode] || MODE_LABEL.summary} · Stop sharing
                          </button>
                        ) : (
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              onClick={() => share(id, "summary", c.source)}
                              disabled={busyId !== null}
                              style={{
                                flex: 1, padding: "9px 10px", background: "none",
                                border: `0.5px solid ${P.border}`, borderRadius: 10,
                                fontSize: 12.5, color: P.text,
                                cursor: busyId !== null ? "default" : "pointer",
                              }}
                            >
                              Share takeaway
                            </button>
                            <button
                              type="button"
                              onClick={() => share(id, "excerpt", c.source)}
                              disabled={busyId !== null}
                              style={{
                                flex: 1, padding: "9px 10px", background: "none",
                                border: `0.5px solid ${P.border}`, borderRadius: 10,
                                fontSize: 12.5, color: P.text,
                                cursor: busyId !== null ? "default" : "pointer",
                              }}
                            >
                              Share full entry
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {/* Prose, so it stays in Reflect/Contextualize register —
                rubric-asserted in the test. The buttons above are
                affordances and are allowed to be imperative. */}
            <p style={{ fontSize: 11.5, color: P.textMute, lineHeight: 1.7, margin: "22px 0 0" }}>
              Entries are chosen one at a time, and sharing can be ended here at
              any point. Ending it takes effect the next time {practitionerName}
              opens your report.
            </p>
          </>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none", border: "none", marginTop: 22, padding: 10,
            color: P.textMid, fontSize: 13.5, cursor: "pointer",
          }}
        >
          Done
        </button>
      </div>
    </div>,
    document.body
  );
}
